import { Box } from '@material-ui/core';
import { SelectableProfile } from '@nemuru-eng/js-api-client';
import { useAuth } from '@nemuru-eng/providers';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Font } from '@/components/shared/components/Font';
import { MoreActionsMenu } from '@/components/shared/components/MoreActionsMenu/MoreActionsMenu';
import { ActionsOption } from '@/components/shared/components/MoreActionsMenu/types/ActionsOption';
import { ProfileAvatar } from '@/components/shared/components/ProfileAvatar';
import { ProfileSwitchOverlay } from '@/components/shared/components/ProfileSwitchOverlay/ProfileSwitchOverlay';
import { Text } from '@/components/shared/components/Text';
import { usePageContext } from '@/contexts/PageContext/PageContext';
import { useProfileSwitch } from '@/hooks/useProfileSwitch';
import { paths } from '@/Routes';

interface ProfileSwitcherMenuProps {
  /** The trigger (the chip's button); MoreActionsMenu attaches the onClick that opens the menu. */
  children: React.ReactElement;
  profiles: SelectableProfile[];
  activeProfileId?: string;
  /** The layout has no Account page: hide "Ir a Cuenta". */
  disabledAccountLink: boolean;
}

/** Upward menu of the bottom chip: the person's profiles (check on the active one), "Ir a Cuenta" and "Cerrar sesión". */
export const ProfileSwitcherMenu = ({
  children,
  profiles,
  activeProfileId,
  disabledAccountLink,
}: ProfileSwitcherMenuProps) => {
  const { logout } = useAuth();
  const { switchingProfile, switchingTo, switchTo } = useProfileSwitch();
  const navigate = useNavigate();
  const {
    actions: { setShowMobileLeftMenu },
  } = usePageContext();

  const profileOptions: ActionsOption[] = profiles.map((profile, index) => ({
    display: (
      <Box display="flex" alignItems="center" gridGap={12} minWidth={0}>
        <ProfileAvatar profile={profile} size={32} />
        <Box display="flex" flexDirection="column" minWidth={0}>
          <Font variant="subtitle2" color="textPrimary" noWrap>
            {profile.displayName ?? profile.username}
          </Font>
          <Font variant="caption" color="textSecondary" noWrap>
            {profile.description ?? <Text tId={`user_${profile.userType}`} />}
          </Font>
        </Box>
      </Box>
    ),
    onClick: () => {
      if (profile.id !== activeProfileId) switchTo(profile.id);
    },
    currentSelection: profile.id === activeProfileId,
    disabled: switchingTo !== undefined,
    divider: index === profiles.length - 1,
  }));

  const accountOptions: ActionsOption[] = [
    ...(disabledAccountLink
      ? []
      : [
          {
            display: <Text tId="GO_TO_ACCOUNT" />,
            onClick: () => {
              setShowMobileLeftMenu(false);
              navigate(paths.AccountView);
            },
            iconName: 'manage_accounts',
          },
        ]),
    {
      display: <Text tId="LOGOUT" />,
      onClick: () => {
        logout();
        setShowMobileLeftMenu(false);
        navigate('/');
      },
      iconName: 'exit_to_app',
    },
  ];

  return (
    <>
      <MoreActionsMenu
        actionsOptionsArray={[...profileOptions, ...accountOptions]}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        keepMounted
      >
        {children}
      </MoreActionsMenu>
      <ProfileSwitchOverlay profile={switchingProfile} open={switchingProfile !== undefined} />
    </>
  );
};
